import { HStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IUsageEvent,
  IUsageEventRequestSchema,
  IUsageTypeResSchema,
  UsageEventRequestSchema,
} from '@monetize/types/app';
import {
  adjustTimeZoneOffsetWithUtc,
  toDateTimeShort,
} from '@monetize/utils/core';
import { addMinutes } from 'date-fns/addMinutes';
import { endOfDay } from 'date-fns/endOfDay';
import { fromUnixTime } from 'date-fns/fromUnixTime';
import { subMinutes } from 'date-fns/subMinutes';
import isNil from 'lodash/isNil';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  useCreateUsageRecord,
  useEditUsageRecord,
} from '../../../../api/accountsService';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  MAlert,
  MBox,
  MButton,
  MCenterModal,
  MCheckbox,
  MCustomNumberInput,
  MFormField,
  MInput,
  MStack,
  MText,
} from '../../../../components/Monetize';
import { DatePicker } from '../../../../components/Monetize/DatePicker/DatePicker';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { useCurrencies } from '../../../../hooks/useCurrencies';
import { useUsageEventCustomFields } from '../../../../hooks/useUsageEventCustomFields';
import { useFlags } from '../../../../services/launchDarkly';
import { logger } from '../../../../services/logger';
import { propertyValuesToObject } from './usageProperties.utils';

interface ManualUsageFormProps {
  existingUsage?: IUsageEvent;
  accountId: string;
  subscriptionId: string;
  usageTypes?: IUsageTypeResSchema[] | null;
  periodStartDate: string;
  /** Optional initial timestamp for the date picker. If not provided, defaults to periodStartDate. */
  initialTimestamp?: string;
  earliestAllowedEditDate?: number;
  isOpen: boolean;
  onClose: (didSave?: boolean) => void;
}

export const ManualUsageForm: FC<ManualUsageFormProps> = ({
  existingUsage,
  isOpen,
  onClose,
  subscriptionId,
  usageTypes,
  accountId,
  periodStartDate,
  initialTimestamp,
  earliestAllowedEditDate,
}: ManualUsageFormProps) => {
  const defaultTimestamp = initialTimestamp ?? periodStartDate;
  const [maxDate] = useState(() => endOfDay(new Date()));
  const [loading, setLoading] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [hasPriceOverride, setHasPriceOverride] = useState<boolean>(
    () => !isNil(existingUsage?.ratedPrice),
  );
  const { enableUsageCustomFields } = useFlags();
  const isEdit = !!existingUsage;
  const {
    customFields: usageCustomFields,
    isLoading: isLoadingCustomFields,
    isError: isCustomFieldsError,
  } = useUsageEventCustomFields();
  const [propertyValues, setPropertyValues] = useState<Record<string, string>>(
    () => existingUsage?.properties ?? {},
  );
  const { mutateAsync: doPostSubscriptionUsage } = useCreateUsageRecord();
  const { mutateAsync: doUpdateSubscriptionUsage } = useEditUsageRecord();
  const { currencyPlaceholder } = useCurrencies();

  const updatePropertyValue = (key: string, value: string) => {
    setPropertyValues((prev) => ({ ...prev, [key]: value }));
  };

  // On edit, show whatever is stored (properties are write-on-create) with the
  // configured label when one exists; on create, one input per configured field.
  const storedPropertyEntries = Object.entries(existingUsage?.properties ?? {});
  const showPropertiesSection =
    enableUsageCustomFields &&
    (isEdit ? storedPropertyEntries.length > 0 : usageCustomFields.length > 0);

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(UsageEventRequestSchema),
    mode: 'onChange',
    defaultValues: existingUsage
      ? {
          ...existingUsage,
          timestamp: adjustTimeZoneOffsetWithUtc(
            fromUnixTime(existingUsage.timestamp),
          ),
        }
      : {
          id: '',
          subscriptionId,
          accountId,
          unitsConsumed: 1,
          timestamp: defaultTimestamp,
          usageTypeId: usageTypes?.[0]?.id,
        },
  });

  useEffect(() => {
    setValue('accountId', accountId || '');
    setValue('subscriptionId', subscriptionId || '');
  }, [accountId, setValue, subscriptionId]);

  const isValid = Object.keys(errors).length === 0;
  // On create, the property inputs are the only source of custom field values
  // and properties are write-on-create, so saving before the configured fields
  // are known would omit them with no way to add them later.
  const showCustomFieldsError =
    !isEdit && enableUsageCustomFields && isCustomFieldsError;
  const isWaitingOnCustomFields =
    !isEdit && (isLoadingCustomFields || isCustomFieldsError);

  const reset = () => {
    setValue('id', '');
    setValue('unitsConsumed', 1);
    setValue('timestamp', defaultTimestamp);
    setValue('ratedPrice', null);
    setDisabled(false);
  };

  const onSubmit = async (data: IUsageEventRequestSchema) => {
    setLoading(true);
    try {
      if (existingUsage?.id) {
        await doUpdateSubscriptionUsage({
          id: existingUsage.id,
          payload: {
            unitsConsumed: data.unitsConsumed,
            // NOTE: the null value of ratedPrice is zero(0) for being a decimal input field
            // That's why i have to check this
            ratedPrice: (data.ratedPrice ?? 0) > 0 ? data.ratedPrice : null,
          },
        });
      } else {
        const properties = enableUsageCustomFields
          ? propertyValuesToObject(propertyValues)
          : undefined;
        await doPostSubscriptionUsage([{ ...data, properties }]);
      }
    } catch (err) {
      // handle err
      handleApiErrorToast(err);
      setLoading(false);
      return;
    }
    reset();
    setLoading(false);
    onClose(true);
  };

  function onError(err: any) {
    logger.error(err, errors);
  }

  function handleGenerateId() {
    setValue('id', uuidv4().slice(0, 23), { shouldValidate: true });
  }

  const timestamp = watch('timestamp');

  const getTimestampHint = () => {
    const tempDate =
      timestamp instanceof Date ? timestamp : new Date(timestamp);
    const offset = tempDate.getTimezoneOffset();
    const date =
      Math.sign(offset) === -1
        ? addMinutes(tempDate, Math.abs(offset))
        : subMinutes(tempDate, offset);
    return toDateTimeShort(date);
  };
  const shouldShowInvoiceWarning =
    earliestAllowedEditDate &&
    timestamp &&
    (typeof timestamp === 'number'
      ? timestamp < earliestAllowedEditDate
      : new Date(timestamp) < fromUnixTime(earliestAllowedEditDate));

  if (!usageTypes || usageTypes.length === 0) {
    return null;
  }

  const hasSingleUsageType = usageTypes.length === 1;

  return (
    <MCenterModal
      isOpen={isOpen}
      trapFocus
      onClose={() => {
        onClose();
        reset();
      }}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      modalTitle={existingUsage ? 'Edit Usage' : 'Add Usage'}
      size="sm"
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton
            variant="cancel"
            isDisabled={loading}
            minW="auto"
            onClick={() => onClose()}
          >
            Cancel
          </MButton>
          <MButton
            form="manual-usage-form"
            data-testid="manual-usage-form-submit"
            variant="primary"
            isLoading={loading}
            isDisabled={
              !isValid || loading || disabled || isWaitingOnCustomFields
            }
            minW="auto"
            type="submit"
          >
            Save
          </MButton>
        </MStack>
      )}
    >
      <MBox>
        <form
          id="manual-usage-form"
          data-testid="manual-usage-form"
          onSubmit={handleSubmit(onSubmit, onError)}
        >
          <MStack>
            {shouldShowInvoiceWarning && (
              <MAlert
                alertIconProps={{ boxSize: 4 }}
                alertDescriptionProps={{ fontSize: 'sm' }}
                alignSelf="stretch"
                type="warning"
              >
                Usage is being added to a period that has already been invoiced.
                Existing invoices won't change, but you can apply a credit or
                issue a credit and re-bill if needed.
              </MAlert>
            )}

            <MFormField
              error={errors?.timestamp}
              label="Usage Timestamp (UTC)"
              isRequired={!existingUsage}
              hint={`Local time: ${getTimestampHint()}`}
            >
              <Controller
                name="timestamp"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    includeTime
                    maxDate={maxDate}
                    isDisabled={loading || !!existingUsage}
                    isReadOnly={!!existingUsage}
                    {...field}
                  />
                )}
              />
            </MFormField>
            <HStack gap="4" alignItems="end">
              <MFormField
                error={errors?.id}
                label="Usage ID"
                isRequired={!existingUsage}
                tooltip={
                  existingUsage
                    ? undefined
                    : 'The Usage ID must be a unique value which will be used to identify the usage record. We can generate an ID for you if you click “Generate”.'
                }
                formErrorMessageProps={{ position: 'absolute', mt: '36px' }}
              >
                <Controller
                  name="id"
                  control={control}
                  render={({ field: { value, ...rest } }) => (
                    <MInput
                      placeholder="Provide a unique id"
                      value={value || ''}
                      {...rest}
                      isDisabled={!!existingUsage || loading}
                      isReadOnly={!!existingUsage}
                      variant={existingUsage ? 'readonly' : undefined}
                    />
                  )}
                />
              </MFormField>
              {!existingUsage && (
                <MButton
                  variant="secondary"
                  size="sm"
                  isDisabled={loading}
                  onClick={handleGenerateId}
                >
                  Generate
                </MButton>
              )}
            </HStack>
            <HStack gap="6" alignItems="start">
              <MFormField
                error={errors?.unitsConsumed}
                label="Unit(s)"
                isRequired
              >
                <Controller
                  name="unitsConsumed"
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <MCustomNumberInput
                      inputMode="decimal"
                      placeholder="Enter Units"
                      isDisabled={loading}
                      onChange={(
                        valueAsString: string,
                        valueAsNumber: number,
                      ) => onChange(valueAsString)}
                      {...rest}
                    />
                  )}
                />
              </MFormField>

              <MFormField error={errors?.usageTypeId} label="Usage Type" ml={6}>
                <Controller
                  name="usageTypeId"
                  control={control}
                  render={({ field }) => (
                    <MCustomSelect
                      itemTitle="name"
                      itemValue="id"
                      items={usageTypes}
                      placeholder="Select"
                      isDisabled={loading || hasSingleUsageType}
                      isReadOnly={hasSingleUsageType}
                      {...field}
                    />
                  )}
                />
              </MFormField>
            </HStack>
            {!existingUsage && (
              <MFormField>
                <MCheckbox
                  isChecked={hasPriceOverride}
                  data-testid="price-override-checkbox"
                  onChange={() => setHasPriceOverride(!hasPriceOverride)}
                >
                  Override the price for the usage record
                </MCheckbox>
              </MFormField>
            )}
            {hasPriceOverride && (
              <HStack maxW="50%">
                <MFormField
                  mr={4}
                  error={errors?.ratedPrice}
                  label="Amount Override"
                  tooltip="If specified, this amount will be charged once for all units on this usage record.  E.g. If Amount Override is $1.00 and quantity is 10 units, the amount charged will be $1.00."
                >
                  <Controller
                    name="ratedPrice"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MCustomNumberInput
                        value={value as any}
                        inputMode="decimal"
                        placeholder={currencyPlaceholder}
                        isDisabled={loading}
                        {...rest}
                      />
                    )}
                  />
                </MFormField>
              </HStack>
            )}
            {showCustomFieldsError && (
              <MAlert
                alertIconProps={{ boxSize: 4 }}
                alertDescriptionProps={{ fontSize: 'sm' }}
                alignSelf="stretch"
                type="error"
                data-testid="usage-custom-fields-error"
              >
                Custom fields couldn't be loaded, so they can't be filled in
                here. Close and reopen this form to try again — custom field
                values can only be set when the usage record is created.
              </MAlert>
            )}
            {showPropertiesSection && (
              <MBox>
                <MText fontWeight="semibold" mb={2}>
                  Custom Fields
                </MText>
                {isEdit ? (
                  <>
                    <MText fontSize="sm" color="fg.muted" mb={2}>
                      Custom fields are set when the usage record is created and
                      cannot be changed.
                    </MText>
                    {storedPropertyEntries.map(([key, value]) => {
                      const field = usageCustomFields.find(
                        (customField) => customField.key === key,
                      );
                      return (
                        <MFormField
                          key={key}
                          label={field?.displayLabel ?? key}
                          tooltip={field?.tooltip || undefined}
                          hint={field ? key : ''}
                        >
                          <MInput
                            value={value}
                            isReadOnly
                            isDisabled
                            variant="readonly"
                          />
                        </MFormField>
                      );
                    })}
                  </>
                ) : (
                  usageCustomFields.map(({ key, displayLabel, tooltip }) => (
                    <MFormField
                      key={key}
                      label={displayLabel}
                      tooltip={tooltip || undefined}
                      hint={key}
                    >
                      <MInput
                        placeholder={displayLabel}
                        value={propertyValues[key] ?? ''}
                        maxLength={255}
                        onChange={(event) =>
                          updatePropertyValue(key, event.target.value)
                        }
                        isDisabled={loading}
                        data-testid={`usage-property-${key}`}
                      />
                    </MFormField>
                  ))
                )}
              </MBox>
            )}
          </MStack>
        </form>
      </MBox>
    </MCenterModal>
  );
};
